<template>
  <div class="whatsapp-message-templates-form">
    <form @submit.prevent="handleSubmit">
      <!-- Franchise Selection -->
      <div class="flex-container">
        <div class="flex-item-full">
          <div class="form-group" :class="{ 'has-error': errors.franquia_ids }">
            <FranchiseSelectMultiple
              v-model="formData.franquia_ids"
              elem-id="whatsapp-templates-franchise-select"
              :label="t('Franquias')"
              :placeholder="t('Selecione as franquias')"
              :required="true"
              data-testid="whatsapp-templates-franchise-select"
            />
            <small v-if="errors.franquia_ids" class="p-error">{{ errors.franquia_ids }}</small>
          </div>
        </div>
      </div>

      <!-- Template Name -->
      <div class="flex-container">
        <div class="flex-item-full">
          <div class="form-group" :class="{ 'has-error': errors.template_name }">
            <label>{{ t('Nome do Template') }} *</label>
            <input-text
              elem-id="whatsapp-templates-name-input"
              v-model="formData.template_name"
              :placeholder="t('Digite o nome do template')"
              :max-length="255"
              data-testid="whatsapp-templates-name-input"
            />
            <small v-if="errors.template_name" class="p-error">{{ errors.template_name }}</small>
          </div>
        </div>
      </div>

      <div class="flex-container">
        <!-- Template Type -->
        <div class="flex-item-half">
          <div class="form-group" :class="{ 'has-error': errors.template_type }">
            <label>{{ t('Tipo') }} *</label>
            <select-list
              elem-id="whatsapp-templates-type-dropdown"
              v-model="formData.template_type"
              :itens-select="typeOptionsForSelect"
              :placeholder="t('Selecione o tipo')"
              data-testid="whatsapp-templates-type-dropdown"
            />
            <small v-if="errors.template_type" class="p-error">{{ errors.template_type }}</small>
          </div>
        </div>

        <!-- Active Toggle -->
        <div class="flex-item-half">
          <div class="form-group">
            <label>{{ t('Ativo') }}</label>
            <div class="toggle-container">
              <input-toggle
                elem-id="whatsapp-templates-active-toggle"
                v-model="formData.is_active"
                data-testid="whatsapp-templates-active-toggle"
              />
              <span class="toggle-label">{{ formData.is_active ? t('Ativo') : t('Inativo') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Template Content -->
      <div class="flex-container">
        <div class="flex-item-full">
          <div class="form-group" :class="{ 'has-error': errors.template_content }">
            <label>{{ t('Conteudo da Mensagem') }} *</label>
            <input-rich-text
              elem-id="whatsapp-templates-content-textarea"
              v-model="formData.template_content"
              :placeholder="t('Digite o conteudo da mensagem')"
              data-testid="whatsapp-templates-content-textarea"
            />
            <small v-if="errors.template_content" class="p-error">{{ errors.template_content }}</small>
          </div>
        </div>
      </div>

      <!-- Media Upload (only shown when type is NOT text) -->
      <div v-if="showMediaUpload" class="flex-container">
        <div class="flex-item-full">
          <div class="form-group" :class="{ 'has-error': errors.media }">
            <label>{{ t('Arquivo de Midia') }} {{ !formData.media_url ? '*' : '' }}</label>
            <input
              type="file"
              :accept="mediaAcceptTypes"
              class="media-file-input"
              data-testid="whatsapp-templates-media-upload"
              @change="handleFileChange"
            />
            <small v-if="errors.media" class="p-error">{{ errors.media }}</small>
          </div>
        </div>
      </div>

      <!-- Media Preview (only shown when there is a media URL) -->
      <div v-if="showMediaPreview" class="flex-container">
        <div class="flex-item-full">
          <div class="form-group">
            <label>{{ t('Preview da Midia') }}</label>
            <div class="media-preview" data-testid="template-media-preview">
              <img
                v-if="formData.template_type === 'image' && formData.media_url"
                :src="formData.media_url"
                :alt="t('Preview da imagem')"
                class="media-preview-image"
              />
              <video
                v-else-if="formData.template_type === 'video' && formData.media_url"
                :src="formData.media_url"
                controls
                class="media-preview-video"
              />
              <audio
                v-else-if="formData.template_type === 'audio' && formData.media_url"
                :src="formData.media_url"
                controls
                class="media-preview-audio"
              />
              <span v-if="formData.media_url" class="media-url-text">{{ formData.media_url }}</span>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import { useWhatsappMessageTemplates } from '../../composables/useWhatsappMessageTemplates';
import FranchiseSelectMultiple from '../../inputs/FranchiseSelectMultiple.vue';

type TemplateType = 'text' | 'image' | 'video' | 'audio';

interface TemplateApiData {
  template_name?: string;
  template_type?: TemplateType;
  template_content?: string;
  template_media_url?: string;
  media_url?: string;
  is_active?: number | boolean | string;
  franquia_ids?: Array<number | string>;
}

interface TemplatePayload {
  template_id?: number;
  template_name: string;
  template_type: TemplateType;
  template_content: string;
  template_media_url: string;
  is_active: 0 | 1;
  franquia_ids: number[];
}

interface UploadMediaResponse {
  media_url?: string;
  url?: string;
}

const { t } = useI18n();
const {
  fetchTemplate,
  saveTemplate,
  uploadMedia
} = useWhatsappMessageTemplates();

const props = withDefaults(
  defineProps<{
    templateId?: number | string | null;
    isEditMode?: boolean;
  }>(),
  {
    templateId: null,
    isEditMode: false
  }
);

const emit = defineEmits<{
  (e: 'submit-success'): void;
  (e: 'submit-error', error: unknown): void;
}>();

// Convert templateId to number for consistency
const templateIdNumber = computed<number | null>(() => {
  if (!props.templateId) return null;
  const num = typeof props.templateId === 'string' ? parseInt(props.templateId, 10) : props.templateId;
  log('WhatsappMessageTemplatesForm', 'templateIdNumber', 'Converted ID', { original: props.templateId, converted: num });
  return num;
});

// Form data
const formData = reactive<{
  template_name: string;
  template_type: TemplateType;
  template_content: string;
  media_url: string;
  is_active: boolean;
  franquia_ids: Array<number | string>;
}>({
  template_name: '',
  template_type: 'text',
  template_content: '',
  media_url: '',
  is_active: true,
  franquia_ids: []
});

// Form state
const errors = reactive<{
  template_name: string;
  template_type: string;
  template_content: string;
  media: string;
  franquia_ids: string;
}>({
  template_name: '',
  template_type: '',
  template_content: '',
  media: '',
  franquia_ids: ''
});

const isLoadingForm = ref<boolean>(false);
const selectedFile = ref<File | null>(null);

// Type options for dropdown
const typeOptions = computed<Array<{ label: string; value: TemplateType }>>(() => [
  { label: t('Texto'), value: 'text' },
  { label: t('Imagem'), value: 'image' },
  { label: t('Video'), value: 'video' },
  { label: t('Audio'), value: 'audio' }
]);

// Same options adapted to the select-list {ID, Name} shape
const typeOptionsForSelect = computed<Array<{ ID: TemplateType; Name: string }>>(() =>
  typeOptions.value.map((option) => ({ ID: option.value, Name: option.label }))
);

// Computed - show media upload when type is not text
const showMediaUpload = computed<boolean>(() => {
  return formData.template_type !== 'text';
});

// Computed - show media preview when there is a media URL and type is not text
const showMediaPreview = computed<boolean>(() => {
  return formData.template_type !== 'text' && !!formData.media_url;
});

// Computed - accepted file types based on template type
const mediaAcceptTypes = computed<string>(() => {
  const acceptMap: Record<Exclude<TemplateType, 'text'>, string> = {
    image: 'image/*',
    video: 'video/*',
    audio: 'audio/*'
  };
  return formData.template_type === 'text'
    ? ''
    : acceptMap[formData.template_type] || '';
});

// Watch template_type changes - clear media when switching to text
watch(() => formData.template_type, (newType, oldType) => {
  log('WhatsappMessageTemplatesForm', 'watch:template_type', 'Type changed', { oldType, newType });
  if (newType === 'text') {
    formData.media_url = '';
    selectedFile.value = null;
  }
});

// Methods
const handleFileChange = (event: Event): void => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0] ?? null;
  if (file) {
    selectedFile.value = file;
    log('WhatsappMessageTemplatesForm', 'handleFileChange', 'File selected', {
      name: file.name,
      size: file.size,
      type: file.type
    });
  }
};

const loadForEdit = async (): Promise<void> => {
  if (!templateIdNumber.value) {
    logWarn('WhatsappMessageTemplatesForm', 'loadForEdit', 'No templateId provided');
    return;
  }

  log('WhatsappMessageTemplatesForm', 'loadForEdit', 'Loading template for edit', templateIdNumber.value);
  isLoadingForm.value = true;

  try {
    const template = (await fetchTemplate(templateIdNumber.value)) as TemplateApiData | null;
    log('WhatsappMessageTemplatesForm', 'loadForEdit', 'Loaded template data', template);

    if (!template) {
      throw new Error('Dados do template nao encontrados');
    }

    // Populate form
    formData.template_name = template.template_name || '';
    formData.template_type = (template.template_type as TemplateType) || 'text';
    formData.template_content = template.template_content || '';
    formData.media_url = template.template_media_url || template.media_url || '';
    formData.is_active = template.is_active == 1 || template.is_active === true || template.is_active === '1';
    formData.franquia_ids = template.franquia_ids || [];

    // Clear errors
    clearErrors();
  } catch (error) {
    const err = error as Error;
    logError('WhatsappMessageTemplatesForm', 'loadForEdit', 'Error loading template', {
      templateId: templateIdNumber.value,
      error: err.message
    });
    eventBus.emit('globalMessage.error', t('Erro ao carregar template') + ': ' + err.message);
    emit('submit-error', err);
  } finally {
    isLoadingForm.value = false;
  }
};

const validateForm = (): boolean => {
  log('WhatsappMessageTemplatesForm', 'validateForm', 'Validating form');
  clearErrors();
  let isValid = true;

  if (!formData.template_name || formData.template_name.trim() === '') {
    errors.template_name = t('Nome e obrigatorio');
    isValid = false;
  }

  if (!formData.template_content || formData.template_content.trim() === '') {
    errors.template_content = t('Conteudo e obrigatorio');
    isValid = false;
  }

  // Media is required for non-text types (either existing URL or new file)
  if (formData.template_type !== 'text' && !formData.media_url && !selectedFile.value) {
    errors.media = t('Arquivo de midia e obrigatorio para este tipo de template');
    isValid = false;
  }

  if (!formData.franquia_ids || formData.franquia_ids.length === 0) {
    errors.franquia_ids = t('Selecione ao menos uma franquia');
    isValid = false;
  }

  log('WhatsappMessageTemplatesForm', 'validateForm', 'Validation result', isValid);
  return isValid;
};

const clearErrors = (): void => {
  errors.template_name = '';
  errors.template_type = '';
  errors.template_content = '';
  errors.media = '';
  errors.franquia_ids = '';
};

const resetForm = (): void => {
  log('WhatsappMessageTemplatesForm', 'resetForm', 'Resetting form');
  formData.template_name = '';
  formData.template_type = 'text';
  formData.template_content = '';
  formData.media_url = '';
  formData.is_active = true;
  formData.franquia_ids = [];
  selectedFile.value = null;
  clearErrors();
};

const handleSubmit = async (): Promise<void> => {
  log('WhatsappMessageTemplatesForm', 'handleSubmit', 'Form submitted');

  if (!validateForm()) {
    logWarn('WhatsappMessageTemplatesForm', 'handleSubmit', 'Validation failed');
    return;
  }

  isLoadingForm.value = true;

  try {
    // If there is a new media file, upload it first
    let mediaUrl: string = formData.media_url;
    if (selectedFile.value && formData.template_type !== 'text') {
      log('WhatsappMessageTemplatesForm', 'handleSubmit', 'Uploading media file first');
      const uploadResult = (await uploadMedia(selectedFile.value, formData.template_type)) as UploadMediaResponse | string;
      if (typeof uploadResult === 'string') {
        mediaUrl = uploadResult;
      } else {
        mediaUrl = uploadResult.media_url || uploadResult.url || '';
      }
      log('WhatsappMessageTemplatesForm', 'handleSubmit', 'Media uploaded', { mediaUrl });
    }

    const payload: TemplatePayload = {
      template_name: formData.template_name.trim(),
      template_type: formData.template_type,
      template_content: formData.template_content.trim(),
      template_media_url: formData.template_type !== 'text' ? mediaUrl : '',
      is_active: formData.is_active ? 1 : 0,
      franquia_ids: formData.franquia_ids.map(Number)
    };

    if (props.isEditMode && templateIdNumber.value) {
      payload.template_id = templateIdNumber.value;
      log('WhatsappMessageTemplatesForm', 'handleSubmit', 'Updating template', payload);
      await saveTemplate(payload);
      eventBus.emit('globalMessage.success', t('Template atualizado com sucesso'));
    } else {
      log('WhatsappMessageTemplatesForm', 'handleSubmit', 'Creating template', payload);
      await saveTemplate(payload);
      eventBus.emit('globalMessage.success', t('Template criado com sucesso'));
    }

    emit('submit-success');
    resetForm();
  } catch (error) {
    const err = error as Error;
    logError('WhatsappMessageTemplatesForm', 'handleSubmit', 'Error submitting form', err);
    eventBus.emit('globalMessage.error',
      props.isEditMode
        ? t('Erro ao atualizar template') + ': ' + err.message
        : t('Erro ao criar template') + ': ' + err.message
    );
    emit('submit-error', err);
  } finally {
    isLoadingForm.value = false;
  }
};

// Watch for changes in templateId to reload data
watch(() => props.templateId, async (newId) => {
  log('WhatsappMessageTemplatesForm', 'watch', 'templateId changed', { newId, isEditMode: props.isEditMode });
  if (newId && props.isEditMode) {
    await nextTick();
    await loadForEdit();
  }
});

// Lifecycle
onMounted(() => {
  log('WhatsappMessageTemplatesForm', 'mounted', 'Component mounted', {
    templateId: props.templateId,
    templateIdNumber: templateIdNumber.value,
    isEditMode: props.isEditMode
  });

  if (props.isEditMode && templateIdNumber.value) {
    void loadForEdit();
  }
});

// Expose methods for parent components
defineExpose({
  loadForEdit,
  handleSubmit,
  resetForm,
  validateForm
});
</script>

<style scoped>
.whatsapp-message-templates-form {
  padding: 15px;
}

.flex-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
}

.flex-item-half {
  flex: 1 1 calc(50% - 8px);
  min-width: 200px;
}

.flex-item-full {
  flex: 1 1 100%;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.has-error :deep(.p-inputtext),
.has-error :deep(.p-select),
.has-error :deep(.p-textarea) {
  border-color: #ef4444;
}

.p-error {
  color: #ef4444;
  display: block;
  margin-top: 4px;
}

.w-full {
  width: 100%;
}

.toggle-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-top: 0.25rem;
}

.toggle-label {
  font-size: 0.9rem;
  color: #495057;
}

.media-file-input {
  display: block;
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.9rem;
}

.media-preview {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 1rem;
  background-color: #f8f9fa;
}

.media-preview-image {
  max-width: 100%;
  max-height: 200px;
  object-fit: contain;
  display: block;
  margin-bottom: 0.5rem;
}

.media-preview-video {
  max-width: 100%;
  max-height: 200px;
  display: block;
  margin-bottom: 0.5rem;
}

.media-preview-audio {
  width: 100%;
  margin-bottom: 0.5rem;
}

.media-url-text {
  display: block;
  font-size: 0.8rem;
  color: #6c757d;
  word-break: break-all;
}
</style>
